import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Box } from "@mui/material";
import Styles from "../../Reusable/Forms/BusinessDescriptionForm.module.css";
import { KPIBasketData } from "../Forms/FormSteps/KPIBasket";

interface FormFooterProps {
  activeStep: number;
  steps: string[];
  handleBack: () => void;
  handleSaveDraft: () => void;
  handleNext: () => void;
  handleFinish: () => void;
  isFormFilled: boolean;
  isFileSelected?: boolean;
  loading?: boolean;
  loading1?: boolean;
  isKpiBasketFilled?: boolean;
  isCriticalTableFilled?: boolean;
  formStatus: { [key: string]: string };
  isLoadingPreview?: boolean;
  accordions?: any[];
  kpiBasketData: KPIBasketData[];
}

interface FormStatus {
  [key: string]: string;
}

const FormFooter: React.FC<FormFooterProps> = ({
  activeStep,
  steps,
  handleBack,
  handleSaveDraft,
  handleNext,
  handleFinish,
  isFormFilled,
  isFileSelected,
  loading,
  loading1,
  isKpiBasketFilled,
  isCriticalTableFilled,
  formStatus,
  isLoadingPreview,
  accordions,
  kpiBasketData,
}) => {
  const navigate = useNavigate();

  const areAllPreviousStepsCompleted = (
    currentStep: number,
    formStatus: FormStatus
  ) => {
    const stepStatusKeys = [
      "bd_stage", // Business Description
      "job_hie_stage", // Job Hierarchy
      "kpi_basket_stage", // KPI Basket
      "critical_matrix_stage", // Critical Matrix
    ];

    // Check all steps before the current one
    for (let i = 0; i < currentStep; i++) {
      const status = formStatus[stepStatusKeys[i]];
      if (status !== "Submitted" && status !== "Completed") {
        return false;
      }
    }
    return true;
  };

  const isFormPartiallyFilled = () => {
    if (!kpiBasketData || !kpiBasketData.length) return false;
    const hasAnyValue = Object.entries(kpiBasketData[0]).some(
      ([_, valueArray]) =>
        valueArray && Array.isArray(valueArray) && valueArray[0] !== ""
    );
    return hasAnyValue && !isKpiBasketFilled;
  };

  const isNextButtonDisabled = () => {
    if (loading) return true;
    if (isLoadingPreview) return false;

    switch (activeStep) {
      case 0:
        return !isFormFilled;
      case 1:
        return !isFileSelected;
      case 2:
        // For KPI Basket, require main form to be filled
        // Accordions are optional for proceeding
        const hasAccordionData = (accordions?.length ?? 0) > 0;
        return !(
          isKpiBasketFilled || 
          (hasAccordionData && !isFormPartiallyFilled())
        );

      case 3:
        return (
          !isCriticalTableFilled ||
          !areAllPreviousStepsCompleted(activeStep, formStatus)
        );
      default:
        return false;
    }
  };

  const isSaveDraftDisabled = () => {
    console.log("isCriticalTableFilled", isCriticalTableFilled);
    if (loading1) return true;
    if (isLoadingPreview) return true;

    // Special case for KPI Basket step (activeStep === 2)
    if (activeStep === 2) {
      // Disable "Save Draft" ONLY if main form is filled AND accordions exist
      if (isKpiBasketFilled && (accordions?.length ?? 0) > 0) {
        return true;
      }

      // Otherwise, enable "Save Draft" (if form is incomplete, even with accordions)
      return false;
    }

    // if (activeStep === 3) {
    //     console.log("isCriticalTableFilled", isCriticalTableFilled);

    //     // For Critical Table step, disable save draft if both critical table and KPI basket are filled
    //     if (!isCriticalTableFilled && isKpiBasketFilled) return false;
    // }
    // Always disable for step 3
    if (activeStep === 3) {
      return true;
    }
    // For other steps, disable save draft when both critical table and KPI basket are filled
    if (isCriticalTableFilled && isKpiBasketFilled) return true;

    // Default behavior - enable save draft if next button would be disabled
    return !isNextButtonDisabled();
  };

  function handleCancel() {
    navigate(`/operations/management/business-description`, {
      // state: { functionL1, functionL2, management }
    });
  }

  return (
    <div className={Styles.footerForm}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: "20px" }}>
          <Button
            variant="outlined"
            onClick={() => handleCancel()}
            sx={{ color: "#000" }}
          >
            Cancel
          </Button>
          {activeStep !== 0 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ color: "#000" }}
            >
              Back
            </Button>
          )}
        </Box>
        <Box>
          {activeStep !== 3 && (
            <Button
              variant="outlined"
              sx={{ mr: 1, color: "#000" }}
              onClick={handleSaveDraft}
              disabled={isSaveDraftDisabled()}
            >
              {loading1 ? "processing..." : "Save Draft"}
            </Button>
          )}

          {activeStep === steps.length - 2 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleFinish}
              disabled={
                loading ||
                loading1 ||
                !isCriticalTableFilled ||
                !areAllPreviousStepsCompleted(activeStep, formStatus)
              }
            >
              {loading ? "processing..." : "Finish"}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={isNextButtonDisabled()}
            >
              {loading ? "processing..." : "Save & Next"}
            </Button>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default FormFooter;
